import React, { useEffect } from 'react';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { generatePath, To, useNavigate } from 'react-router-dom';

import { BackgroundCheckStatusIds, CheckrRequestStatusIds, SitterProfileStatus } from 'us-web-services';
import { tests } from 'us-react-components/dist/constants/Tests';
import { ChecklistItem } from 'us-react-components/dist/ChecklistItem/ChecklistItem';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';


interface Props {
  profileStatus: SitterProfileStatus;
}

const CHECKR_CANDIDATE_LOGIN_URL = 'https://candidate.checkr.com/login';

const ApprovalChecklist = ({ profileStatus }: Props): JSX.Element => {
  const { services: { tracker }, stores: { sitter, user } } = useDependencies();
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id } = sitter.sitter;

  useEffect(() => {
    sitter.getBackgroundCheck();
  }, []);

  const trackAndGo = (elementId: string, route: To): void => {
    tracker.track('clicked-button', { elementId });
    navigate(route);
  };

  const trackAndOpen = (elementId: string, uri: string): void => {
    tracker.track('clicked-button', { elementId });
    window.open(uri, '_blank', 'noreferrer');
  };

  const invitationCompletedReportPending = (): boolean => {
    const bgStatusId = sitter.backgroundCheck?.status?.id ?? BackgroundCheckStatusIds.UNKNOWN;

    return sitter.backgroundCheck?.invitationStatusId === CheckrRequestStatusIds.COMPLETED
      && [BackgroundCheckStatusIds.PENDING, BackgroundCheckStatusIds.SUBMITTED].includes(bgStatusId);
  }

  const invitationExpired = (): boolean => sitter.backgroundCheck?.invitationStatusId === CheckrRequestStatusIds.EXPIRED;

  const getBackgroundCheckTitle = (): string => invitationCompletedReportPending()
    ? 'components.approvalChecklist.backgroundCheckWaiting'
    : 'components.approvalChecklist.backgroundCheck';

  const submitBackgroundCheck = (): void => {
    if (profileStatus.backgroundCheckCleared?.url && !invitationExpired()) {
      if (invitationCompletedReportPending()) {
        trackAndOpen('approval-checklist-background-check-login', CHECKR_CANDIDATE_LOGIN_URL);
      } else {
        trackAndOpen('approval-checklist-background-check-complete', profileStatus.backgroundCheckCleared?.url);
      }
    } else if (invitationExpired()) {
      trackAndGo('approval-checklist-background-check-finish', Routes.backgroundCheckFinish);
    } else {
      trackAndGo('approval-checklist-background-check-buy', user.inTestGroup(tests.CAREGIVER_PAYWALL_STRIPE_PAYMENT) ? Routes.caregiver.buyBackgroundCheck : Routes.backgroundCheckBuy);
    }
  };

  const hasPhoto = (): boolean => !!sitter.sitter.pictureUrl && sitter.sitter.pictureUrl.indexOf('us-default-avatar') === -1;

  return (
    <div>
      <h4 className='my-1 font-bold text-base'>{t('components.approvalChecklist.heading')}</h4>
      <ChecklistItem
        title={t('components.approvalChecklist.purchase')}
        completed={profileStatus.backgroundCheckPurchased?.complete}
        disabled={profileStatus.backgroundCheckPurchased?.complete}
        onClick={(): void => trackAndGo('approval-checklist-purchase-membership', user.inTestGroup(tests.CAREGIVER_PAYWALL_STRIPE_PAYMENT) ? Routes.caregiver.buyBackgroundCheck : Routes.backgroundCheckBuy)}
      />
      <ChecklistItem
        title={t(getBackgroundCheckTitle())}
        completed={profileStatus.backgroundCheckCleared?.complete}
        disabled={profileStatus.backgroundCheckCleared?.complete}
        onClick={(): void => submitBackgroundCheck()}
      />
      <ChecklistItem
        title={t('components.approvalChecklist.tellFamilies')}
        completed={!!sitter.sitter.about?.length}
        onClick={(): void => trackAndGo('tell-families', {
          pathname: generatePath(Routes.sitter.profile, { id }),
          search: 'modal=about',
        })}
      />
      <ChecklistItem
        title={t('components.approvalChecklist.photoGuidelines')}
        completed={hasPhoto()}
        onClick={(): void => trackAndGo('approval-checklist-photo-guidelines', Routes.sitter.registration.photo)}
      />
      <ChecklistItem
        title={t('components.approvalChecklist.setRates')}
        completed={!!sitter.sitter.rates && (sitter.sitter.rates[0]?.minimumRate ?? 0) > 0}
        onClick={(): void => trackAndGo('approval-checklist-set-rates', {
          pathname: generatePath(Routes.sitter.profile, { id }),
          search: 'modal=rates',
        })}
      />
    </div>
  );
};

export { ApprovalChecklist };
